import type { Worktree, WorktreeDetailResponse } from "@rivets/core";
import { client } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { qk } from "@/lib/query-keys";

/** Writes a worktree into every cache that lists it: the repository's task
 * list and the task's own detail record. */
export function patchWorktreeCaches(worktree: Worktree): void {
  queryClient.setQueryData<{ worktrees: Worktree[] }>(qk.worktrees(worktree.repoId), (old) => old
    ? { worktrees: old.worktrees.map((candidate) => candidate.id === worktree.id ? worktree : candidate) }
    : old);
  queryClient.setQueryData<WorktreeDetailResponse>(qk.worktree(worktree.id), (old) => old ? { ...old, worktree } : old);
}

/**
 * Renames a task optimistically. The new name lands in every list at once and
 * is reverted only if the server refuses; a `worktree.updated` frame confirms
 * it for everyone else. An empty or unchanged name is a no-op.
 */
export async function renameWorktree(worktree: Worktree, nextName: string): Promise<void> {
  const name = nextName.trim();
  if (!name || name === worktree.name) return;
  patchWorktreeCaches({ ...worktree, name });
  try {
    patchWorktreeCaches((await client.updateWorktree(worktree.id, { name })).worktree);
  } catch (error) {
    patchWorktreeCaches(worktree);
    throw error;
  }
}
